import { Injectable, isDevMode } from "@angular/core";
import { Rarity } from "../models/enums";
import { GameItem } from "../models/game-item.models";
import { ItemDataService } from "./item-data.service";
import { FlatItemData } from "../models/item-data-models";
import { ModMatcherService } from "./mod-matcher.service";
import { poe2 } from "../../game";
import { getGameItemExampleName } from "./game-item-display.util";

type FlagMatcher = {
  match: (line: string) => boolean;
  apply: (line: string, item: GameItem) => void;
};

type KeyValueMatcher = {
  prefix: string;
  assign: (suffix: string, item: GameItem) => void;
};

const flagMatchers: FlagMatcher[] = [
  {
    match: (l) => l === "Corrupted",
    apply: (_l, item) => {
      item.corrupted = true;
    },
  },
  {
    match: (l) => l === "Twice Corrupted",
    apply: (_l, item) => {
      item.corrupted = true;
      item.twiceCorrupted = true;
    },
  },
  {
    match: (l) => l === "Mirrored",
    apply: (_l, item) => {
      item.mirrored = true;
    },
  },
  {
    match: (l) => l === "Unidentified" || /^Unidentified \(Tier \d+\)$/.test(l),
    apply: (l, item) => {
      item.identified = false;
      const tierMatch = l.match(/^Unidentified \(Tier (\d+)\)$/);
      if (tierMatch) {
        item.unidentifiedItemTier = parseInt(tierMatch[1], 10);
      }
    },
  },
  {
    match: (l) => l === "Identified",
    apply: (_l, item) => {
      item.identified = true;
    },
  },
  {
    match: (l) => l === "Synthesised" || l === "Synthesised Item",
    apply: (_l, item) => {
      item.synthesisedItem = true;
    },
  },
  {
    match: (l) => l === "Fractured Item" || l === "Fractured",
    apply: (_l, item) => {
      item.fracturedItem = true;
    },
  },
  {
    match: (l) => l === "Shaper Item",
    apply: (_l, item) => {
      item.shaperItem = true;
      // also record influence
      item.hasInfluence = "Shaper";
    },
  },
  {
    match: (l) => l === "Elder Item",
    apply: (_l, item) => {
      item.elderItem = true;
      item.hasInfluence = "Elder";
    },
  },
  {
    match: (l) => l === "Crusader Item",
    apply: (_l, item) => {
      item.hasInfluence = "Crusader";
    },
  },
  {
    match: (l) => l === "Hunter Item",
    apply: (_l, item) => {
      item.hasInfluence = "Hunter";
    },
  },
  {
    match: (l) => l === "Redeemer Item",
    apply: (_l, item) => {
      item.hasInfluence = "Redeemer";
    },
  },
  {
    match: (l) => l === "Warlord Item",
    apply: (_l, item) => {
      item.hasInfluence = "Warlord";
    },
  },
  {
    match: (l) => l === "Blighted Map",
    apply: (_l, item) => {
      item.blightedMap = true;
    },
  },
  {
    match: (l) => l === "Blight‑ravaged Map" || l === "Blight-ravaged Map",
    apply: (_l, item) => {
      // PoE uses either hyphen or en dash – treat both as uber blighted
      item.uberBlightedMap = true;
    },
  },
  {
    match: (l) => l === "Uber Blighted Map",
    apply: (_l, item) => {
      item.uberBlightedMap = true;
    },
  },
  {
    match: (l) => l === "Shaped Map",
    apply: (_l, item) => {
      item.shapedMap = true;
    },
  },
  {
    match: (l) => l === "Elder Map",
    apply: (_l, item) => {
      item.elderMap = true;
    },
  },
  {
    match: (l) => l === "Scourged" || l === "Scourged Item",
    apply: (_l, item) => {
      item.scourged = true;
    },
  },
  {
    match: (l) => l === "Foulborn" || l === "Foulborn Item",
    apply: (_l, item) => {
      item.foulborn = true;
    },
  },
  {
    match: (l) => /Crucible Passive Tree/.test(l),
    apply: (_l, item) => {
      item.hasCruciblePassiveTree = true;
    },
  },
  {
    match: (l) => /Zana.*Memory/.test(l) || l === "Zana's Memory",
    apply: (_l, item) => {
      item.zanaMemory = true;
    },
  },
  {
    match: (l) => /Replica/.test(l),
    apply: (_l, item) => {
      item.replica = true;
    },
  },
  {
    match: (l) => /Vaal Unique/.test(l),
    apply: (_l, item) => {
      item.isVaalUnique = true;
    },
  },
];

const keyValueMatchers: KeyValueMatcher[] = [
  {
    prefix: "Item Level: ",
    assign: (suffix, item) => {
      const num = Number(suffix);
      if (!isNaN(num)) item.itemLevel = num;
    },
  },
  {
    prefix: "Gem Level: ",
    assign: (suffix, item) => {
      const num = Number(suffix);
      if (!isNaN(num)) item.gemLevel = num;
    },
  },
  {
    prefix: "Quality: ",
    assign: (suffix, item) => {
      // remove percent sign and plus/minus
      const cleaned = suffix.replace(/[^\d-]/g, "");
      const num = parseInt(cleaned, 10);
      if (!isNaN(num)) item.quality = num;
    },
  },
  {
    prefix: "Energy Shield: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.baseEnergyShield = num;
    },
  },
  {
    prefix: "Armour: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.baseArmour = num;
    },
  },
  {
    prefix: "Evasion Rating: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.baseEvasion = num;
    },
  },
  {
    prefix: "Ward: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.baseWard = num;
    },
  },
  {
    prefix: "Stack Size: ",
    assign: (suffix, item) => {
      // suffix looks like "current/max", e.g. "1/20" or just a number.
      const parts = suffix.split("/");
      const value = parseInt(parts[0].replace(/[^\d]/g, ""), 10);
      if (!isNaN(value)) item.stackSize = value;
    },
  },
  {
    prefix: "Drop Level: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.dropLevel = num;
    },
  },
  {
    prefix: "Waystone Tier: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.waystoneTier = num;
    },
  },
  {
    prefix: "Memory Strands: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.memoryStrands = num;
    },
  },
  {
    prefix: "Height: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.height = num;
    },
  },
  {
    prefix: "Width: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.width = num;
    },
  },
  {
    prefix: "Archnemesis Mod: ",
    assign: (suffix, item) => {
      item.archnemesisMod = suffix.trim();
    },
  },
  {
    prefix: "Gem Quality Type: ",
    assign: (suffix, item) => {
      item.gemQualityType = suffix.trim();
      if (suffix.trim().length > 0) {
        item.alternateQuality = true;
      }
    },
  },
  {
    prefix: "Enchantment Passive Num: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.enchantmentPassiveNum = num;
    },
  },
  {
    prefix: "Enchantment Passive Node: ",
    assign: (suffix, item) => {
      item.enchantmentPassiveNode = suffix.trim();
    },
  },
  {
    prefix: "Searing Exarch Implicit: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.hasSearingExarchImplicit = num;
    },
  },
  {
    prefix: "Eater of Worlds Implicit: ",
    assign: (suffix, item) => {
      const num = parseInt(suffix.replace(/[^\d]/g, ""), 10);
      if (!isNaN(num)) item.hasEaterOfWorldsImplicit = num;
    },
  },
  {
    // Granted skills are already shown via static gear data implicits; ignore here
    prefix: "Grants Skill: ",
    assign: (_suffix, _item) => {},
  },
];

@Injectable({
  providedIn: 'root'
})
export class GameItemParserService {
  allItemData!: FlatItemData[];
  private readonly debugMode: boolean;

  constructor(
    private itemDataService: ItemDataService,
    private modMatcherService: ModMatcherService
  ) {
    this.allItemData = itemDataService.getFlatItemData();
    // Enable debug mode in non-production builds
    this.debugMode = isDevMode();
  }

  parseItem(text: string): GameItem {
    // Trim trailing whitespace and normalise line endings.
    const trimmed = text.replace(/\r/g, "").trim();

    // Detect advanced mode (contains curly brackets with mod names)
    const isAdvancedMode = /\{.*\}/.test(trimmed);

    const lines = trimmed.split("\n");
    // Split into sections.  A line consisting solely of hyphens separates
    // sections.  Consecutive separators produce empty sections which we
    // filter out later.
    const sections: string[][] = [];
    let current: string[] = [];
    for (const line of lines) {
      if (/^-{2,}$/.test(line)) {
        // commit existing section if any
        if (current.length) sections.push(current);
        current = [];
      } else {
        current.push(line);
      }
    }
    if (current.length) sections.push(current);

    if (sections.length === 0) {
      throw new Error("No content to parse");
    }

    // Parse nameplate (first section)
    const nameplate = sections.shift()!;
    let itemClass: string | undefined;
    let rarity: Rarity | undefined;
    const remainingNameLines: string[] = [];
    for (const line of nameplate) {
      if (line.startsWith("Item Class:")) {
        itemClass = line.slice("Item Class:".length).trim();
      } else if (line.startsWith("Rarity:")) {
        rarity = line.slice("Rarity:".length).trim() as Rarity;
      } else if (line.length > 0) {
        remainingNameLines.push(line.trim());
      }
    }
    if (!itemClass) {
      throw new Error("Item class not found in item text");
    }
    if (!rarity) {
      // If a rarity is not supplied the item is assumed to be normal.
      rarity = Rarity.Normal;
    }

    let baseType = "";
    let name: string | undefined = undefined;
    if (remainingNameLines.length >= 2) {
      // All but the final line compose the name; the final line is the base type
      name = remainingNameLines.slice(0, -1).join(" ");
      baseType = remainingNameLines[remainingNameLines.length - 1];
    } else if (remainingNameLines.length === 1) {
      baseType = remainingNameLines[0];
    }

    // Parse map tier from base type pattern "Map (Tier X)" and normalize base type
    let parsedMapTier: number | undefined;
    const mapTierMatch = baseType.match(/^Map\s*\(Tier\s+(\d+)\)$/);
    if (mapTierMatch) {
      parsedMapTier = parseInt(mapTierMatch[1], 10);
    }

    // Create the initial GameItem
    const item = new GameItem({ itemClass, baseType, name, rarity });

    if (parsedMapTier !== undefined) {
      item.mapTier = parsedMapTier;
    }

    if (item.baseType === "Shaper Guardian Map") {
      item.hasInfluence = "Shaper";
      item.mapTier = 16;
    }

    // By default we assume an item is identified unless the text says otherwise.
    // `identified` remains undefined if there is no explicit state on the item.
    item.identified = undefined;

    // Track if item was parsed from advanced mode
    item.parsedFromAdvancedMode = isAdvancedMode;

    // Extract mod names from advanced mode if detected
    let extractedModNames: string[] = [];
    if (isAdvancedMode) {
      extractedModNames = this.extractModNamesFromAdvancedMode(lines);
      if (this.debugMode && extractedModNames.length > 0) {
        console.log(`[GameItemParser] Advanced mode detected: extracted ${extractedModNames.length} mod name(s):`, extractedModNames);
      }
    }

    const unknownLines: string[] = [];

    let secondSection = true;
    // Iterate over all remaining sections
    sections.forEach((section) => {
      // PoE1-style requirements section: header line is "Requirements:" alone,
      // followed by individual "Key: Value" lines (Level, Str, Dex, Int).
      if (section.length >= 1 && section[0].trim() === "Requirements:") {
        if (!item.requirements) {
          item.requirements = {};
        }
        for (let i = 1; i < section.length; i++) {
          const reqLine = section[i].trim();
          const reqMatch = reqLine.match(/^(Level|Str|Dex|Int):\s*(\d+)/i);
          if (reqMatch) {
            const num = parseInt(reqMatch[2], 10);
            if (!isNaN(num)) {
              const key = reqMatch[1].toLowerCase() as "level" | "str" | "dex" | "int";
              item.requirements[key] = num;
            }
          }
        }
        secondSection = false;
        return;
      }

      let insideImplicitMod = false;
      section.forEach((line) => {
        const trimmedLine = line.trim();
        // Skip empty lines
        if (!trimmedLine) return;

        // First run through flag matchers
        let matched = false;
        for (const fm of flagMatchers) {
          if (fm.match(trimmedLine)) {
            fm.apply(trimmedLine, item);
            matched = true;
            break;
          }
        }
        if (matched) return;

        // Next run through key/value matchers
        for (const kv of keyValueMatchers) {
          if (trimmedLine.startsWith(kv.prefix)) {
            const suffix = trimmedLine.slice(kv.prefix.length).trim();
            kv.assign(suffix, item);
            matched = true;
            break;
          }
        }
        if (matched) return;

        // Special handling for sockets: these lines start with "Sockets:" and
        // include multiple socket groups.  They need custom parsing because
        // there are two derived numeric properties (total sockets and
        // maximum linked sockets).
        if (trimmedLine.startsWith("Sockets:")) {
          const suffix = trimmedLine.slice("Sockets:".length).trim();
          const { sockets, linked, socketGroup } = this.parseSockets(suffix);
          item.sockets = sockets;
          if (linked > 0) item.linkedSockets = linked;
          if (socketGroup) item.socketGroup = socketGroup;
          matched = true;
          return;
        }

        // Lines that indicate influences but do not start with the exact
        // phrase matched above.  Examples include "Shaper Map" (not item),
        // "Elder Map" and the non‑Elder map influences introduced in
        // later patches.  When such a pattern is seen, record the
        // influence text verbatim.  Only record the first influence to
        // avoid overriding more specific flags such as `shaperItem`.
        if (/Item$/.test(trimmedLine) && /\b(Item)\b/.test(trimmedLine)) {
          const [candidate] = trimmedLine.split(/\s+/);
          if (!item.hasInfluence) {
            item.hasInfluence = candidate.replace(/Item$/, "");
          }
          matched = true;
          return;
        }

        // Check for requirements (e.g., "Requires: Level 70, 121 Str")
        if (trimmedLine.startsWith("Requires:")) {
          // Parse requirements for Level, Str, Dex, Int
          // Format: "Requires: Level 70, 121 Str" or "Requires: Level 70, 121 Str, 45 Dex"
          const requirementsText = trimmedLine.slice("Requires:".length).trim();

          // Initialize requirements object if it doesn't exist
          if (!item.requirements) {
            item.requirements = {};
          }

          // Parse each requirement (split by comma)
          const parts = requirementsText.split(',').map(p => p.trim());
          for (const part of parts) {
            // Match patterns like "Level 70", "121 Str", "45 Dex", "30 Int"
            const levelMatch = part.match(/^Level\s+(\d+)$/i);
            if (levelMatch) {
              const num = parseInt(levelMatch[1], 10);
              if (!isNaN(num)) item.requirements.level = num;
              continue;
            }

            const strMatch = part.match(/^(\d+)\s+Str$/i);
            if (strMatch) {
              const num = parseInt(strMatch[1], 10);
              if (!isNaN(num)) item.requirements.str = num;
              continue;
            }

            const dexMatch = part.match(/^(\d+)\s+Dex$/i);
            if (dexMatch) {
              const num = parseInt(dexMatch[1], 10);
              if (!isNaN(num)) item.requirements.dex = num;
              continue;
            }

            const intMatch = part.match(/^(\d+)\s+Int$/i);
            if (intMatch) {
              const num = parseInt(intMatch[1], 10);
              if (!isNaN(num)) item.requirements.int = num;
              continue;
            }
          }

          matched = true;
          return;
        }

        if (!secondSection) {
          // Skip bracket lines in advanced mode (they've already been processed for mod names)
          if (isAdvancedMode && /^\{.*\}$/.test(trimmedLine)) {
            insideImplicitMod = /Implicit Modifier/.test(trimmedLine);
            return; // Skip this bracket line
          }

          // Collect mod text that belongs to an implicit modifier bracket
          if (insideImplicitMod) {
            if (!item.implicitMods) item.implicitMods = [];
            item.implicitMods.push(trimmedLine);
            return;
          }

          // Anything else is captured as an explicit modifier or enchantment.
          // Heuristically differentiate enchantments based on the presence of
          // "enchant".  Multiple lines are concatenated together using
          // newlines.  If both enchantment and explicit modifiers are found
          // they share the same string; downstream code can split them as
          // needed.
          unknownLines.push(trimmedLine);
        }
      });

      secondSection = false;
    });

    if (unknownLines.length) {
      // Separate implicit/augment mods from other unknown lines
      const implicitMods: string[] = [];
      const augmentMods: string[] = [];
      const otherLines: string[] = [];

      for (const line of unknownLines) {
        if (line.includes("(implicit)")) {
          const cleanedLine = line.replace(/\s*\(implicit\)\s*/g, "").trim();
          if (cleanedLine) {
            implicitMods.push(cleanedLine);
          }
          if (/Area is influenced by The Shaper/.test(line)) {
            item.hasInfluence = "Shaper";
          } else if (/Area is influenced by The Elder/.test(line)) {
            item.hasInfluence = "Elder";
          }
        } else if (line.includes("(rune)")) {
          const cleanedLine = line.replace(/\s*\(rune\)\s*/g, "").trim();
          if (cleanedLine) {
            augmentMods.push(cleanedLine);
          }
        } else {
          otherLines.push(line);
        }
      }

      // Store implicit mods if any were found
      if (implicitMods.length) {
        item.implicitMods = implicitMods;
      }

      // Store augment (rune) mods if any were found
      if (augmentMods.length) {
        item.augmentMods = augmentMods;
      }

      // Handle remaining lines as enchantments or explicit mods
      if (otherLines.length) {
        // Extract lines containing 'click' (case-insensitive) as description
        // Keep only the last one if there are multiple
        // Exclude "Shift click to unstack."
        const clickLines = otherLines.filter((ln) => /click/i.test(ln) && ln.trim() !== "Shift click to unstack.");
        if (clickLines.length > 0) {
          item.description = clickLines[clickLines.length - 1];
        }

        // Remove click lines from otherLines before processing as enchantments/explicit mods
        // Keep "Shift click to unstack." as it's not a description
        const remainingLines = otherLines.filter((ln) => !/click/i.test(ln));

        if (remainingLines.length) {
          if (poe2) {
            const combined = remainingLines.join("\n");
            // Choose whether to store the combined unknown text as an enchantment
            // or an explicit mod.  If any line mentions "enchant" then treat the
            // whole block as an enchantment, otherwise as explicit mod.
            const isEnchant = remainingLines.some((ln) => /enchant/i.test(ln));
            if (isEnchant) {
              item.hasEnchantment = combined;
            } else {
              item.explicitMods = combined.split("\n");
            }
          } else {
            item.hasEnchantment = remainingLines.find((ln) => /enchant/i.test(ln));
            item.explicitMods = remainingLines.filter((ln) => !/enchant/i.test(ln));
          }
        }
      }
    }

    // Some fixes
    if (item.baseType?.startsWith('Superior') || item.baseType?.startsWith('Exceptional')) {
      item.name = item.baseType;
      item.baseType = item.baseType.split(' ').slice(1).join(' ').trim();
    }

    if (item.rarity === 'Magic') {
      item.name = item.baseType;
      // If name contains the word 'of', then strip of that and everything after it
      if (item.baseType.includes('of')) {
        item.baseType = item.baseType.split('of')[0].trim();
      }
      // Check whether this exists in the flat item data; if not, strip off the first word and keep the rest
      const baseType = this.itemDataService.getFlatItemDataByBaseTypeName(item.baseType);
      if (!baseType) {
        item.baseType = item.baseType.split(' ').slice(1).join(' ').trim();
      }
    }

    // Get gear requirements from itemdataservice if not already parsed
    if (item.baseType) {
      const gearData = this.itemDataService.getGear(item.baseType);
      if (gearData) {
        // Initialize requirements object if it doesn't exist
        if (!item.requirements) {
          item.requirements = {};
        }

        // Merge requirements: prefer parsed values, fallback to gear data
        if (gearData.lvl !== undefined && item.requirements.level === undefined) {
          item.requirements.level = gearData.lvl;
        }
        if (gearData.str !== undefined && item.requirements.str === undefined) {
          item.requirements.str = gearData.str;
        }
        if (gearData.dex !== undefined && item.requirements.dex === undefined) {
          item.requirements.dex = gearData.dex;
        }
        if (gearData.int !== undefined && item.requirements.int === undefined) {
          item.requirements.int = gearData.int;
        }

        if (gearData.arm || gearData.es || gearData.ev) {
          // Also set defence values
          item.baseArmour = gearData.arm ?? 0;
          item.baseEnergyShield = gearData.es ?? 0;
          item.baseEvasion = gearData.ev ?? 0;
        }
      }
    }

    if (item.requirements?.level) item.dropLevel = item.requirements.level;

    // Calculate width and height based on item class if not already set
    this.calculateItemDimensions(item);

    // Resolve mod names from explicit mod text
    // If mod names were already extracted from advanced mode, use those instead
    if (extractedModNames.length > 0) {
      item.explicitModNames = extractedModNames;
      if (this.debugMode) {
        console.log(`[GameItemParser] Using mod names from advanced mode: ${item.explicitModNames.length} mod name(s)`, item.explicitModNames);
      }
    } else if (item.explicitMods && item.explicitMods.length > 0) {
      if (this.debugMode) {
        console.log(`[GameItemParser] Resolving mod names for ${item.explicitMods.length} explicit mod(s):`, item.explicitMods);
      }

      const modNames = this.modMatcherService.matchModTexts(item.explicitMods);
      // Remove duplicates while preserving order
      item.explicitModNames = [...new Set(modNames)];

      if (this.debugMode) {
        console.log(`[GameItemParser] Resolved to ${item.explicitModNames.length} unique mod name(s):`, item.explicitModNames);
        if (modNames.length > item.explicitModNames.length) {
          console.log(`[GameItemParser] Note: ${modNames.length - item.explicitModNames.length} duplicate match(es) removed`);
        }
      }

      // TODO: When multiple matches occur (e.g., same values in low-tier two-handed vs high-tier one-handed),
      // we need to disambiguate by looking at the mod slots. For now, we return all matches.
    } else if (this.debugMode) {
      console.log(`[GameItemParser] No explicit mods to resolve`);
    }

    if (item.explicitModNames && item.explicitModNames.length > 0 && item.identified !== false) {
      item.identified = true;
    }

    // If the item's identified state was never set, assume it is
    // identified unless unidentifiable.  Leaving it undefined allows
    // consumer code to decide how to handle absence of information.
    if (item.rarity === Rarity.Normal) {
      const displayName = getGameItemExampleName(item);
      if (displayName !== item.baseType) {
        item.name = displayName;
      }
    }

    return item;
  }

  /**
   * Extracts mod names from advanced mode item text (lines with curly brackets).
   * Only extracts mod names for explicit mods (Prefix/Suffix), not implicit mods.
   *
   * @param lines All lines from the item text
   * @returns Array of extracted mod names
   */
  private extractModNamesFromAdvancedMode(lines: string[]): string[] {
    const modNames: string[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Check if this is a bracket line (starts with { and ends with })
      if (!trimmedLine.startsWith('{') || !trimmedLine.endsWith('}')) {
        continue;
      }

      // Skip implicit mods - we don't extract mod names for them
      if (trimmedLine.includes('Implicit Modifier')) {
        continue;
      }

      // Extract mod name from Prefix or Suffix modifiers
      // Pattern: { Prefix Modifier "ModName" ... } or { Suffix Modifier "ModName" ... }
      // Handle various whitespace and em-dash characters
      const prefixMatch = trimmedLine.match(/\{\s*Prefix\s+Modifier\s+"([^"]+)"\s*/);
      const suffixMatch = trimmedLine.match(/\{\s*Suffix\s+Modifier\s+"([^"]+)"\s*/);

      if (prefixMatch && prefixMatch[1]) {
        modNames.push(prefixMatch[1]);
      } else if (suffixMatch && suffixMatch[1]) {
        modNames.push(suffixMatch[1]);
      }
    }

    return modNames;
  }

  /**
   * Calculates the width and height of an item based on its class.
   * Only sets dimensions if they are not already set (e.g., from parsed item text).
   */
  private calculateItemDimensions(item: GameItem): void {
    // Skip if dimensions are already set
    if (item.width !== undefined && item.height !== undefined) {
      return;
    }

    const itemClass = item.itemClass || '';

    // Get weapon type lists from itemDataService
    const oneHandWeaponTypes = this.itemDataService.getOneHandWeaponTypes().map(w => w.toString());
    const twoHandWeaponTypes = this.itemDataService.getTwoHandedWeaponTypes().map(w => w.toString());

    // Define dimensions per item class
    const dimensionMap: Record<string, { width: number; height: number }> = {
      'Body Armours': { width: 2, height: 3 },
      'Helmets': { width: 2, height: 2 },
      'Gloves': { width: 2, height: 2 },
      'Boots': { width: 2, height: 2 },
      'Belts': { width: 2, height: 1 },
      'Shields': { width: 2, height: 3 },
      'Bucklers': { width: 2, height: 3 },
    };

    // Add weapon types to dimension map
    twoHandWeaponTypes.forEach(weaponType => {
      dimensionMap[weaponType] = { width: 2, height: 4 };
    });
    oneHandWeaponTypes.forEach(weaponType => {
      dimensionMap[weaponType] = { width: 1, height: 3 };
    });

    // Set dimensions if found in map
    const dimensions = dimensionMap[itemClass];
    if (dimensions) {
      item.width = dimensions.width;
      item.height = dimensions.height;
    } else {
      item.width = 1;
      item.height = 1;
    }
  }

  /**
   * Extracts socket information from a string of coloured socket indicators.  A
   * socket string contains one or more groups separated by spaces.  Within
   * each group the individual sockets are separated by hyphens.  The
   * letters themselves are not important for counting; for example
   * "R-G-B" and "GGG" both yield three sockets.  The largest group
   * determines the `linkedSockets` value.  Examples:
   *
   *   "G-G-B R-R" -> sockets = 5, linkedSockets = 3
   *   "B-G-R"     -> sockets = 3, linkedSockets = 3
   *
   * Sockets may also include white sockets denoted by 'W'.  Those count
   * towards the total number of sockets the same as coloured sockets.
   *
   * The largest linked group is converted to the tool's socketGroup format:
   * RGBW for known colors, X for unknown colors.  The socketGroup does not
   * include the L-prefix (that's added when using the socketGroup).
   */
  private parseSockets(value: string): { sockets: number; linked: number; socketGroup?: string } {
    const groups = value.trim().split(/\s+/);
    let totalSockets = 0;
    let maxLinked = 0;
    let largestGroup: string | undefined = undefined;
    for (const group of groups) {
      // remove hyphens
      const cleaned = group.replace(/-/g, "");
      const count = cleaned.length;
      if (count > 0) {
        totalSockets += count;
        if (count > maxLinked) {
          maxLinked = count;
          largestGroup = cleaned;
        }
      }
    }

    // Convert the largest group to tool's socketGroup format
    let socketGroup: string | undefined = undefined;
    if (largestGroup && maxLinked > 0) {
      // Convert socket colors: R->R, G->G, B->B, W->W, anything else->X
      socketGroup = largestGroup
        .split("")
        .map(char => {
          const upper = char.toUpperCase();
          if (upper === "R" || upper === "G" || upper === "B" || upper === "W") {
            return upper;
          } else if (upper === "S") {
            return "W";
          }
          return "X"; // Unknown color becomes X
        })
        .join("");
    }

    return { sockets: totalSockets, linked: maxLinked, socketGroup };
  }
}
